import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  Panel,
  NodeResizer,
  ConnectionLineType,
  Handle,
  Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  IconButton,
  TextField,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Save as SaveIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Fullscreen as FullscreenIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import axios from '../api/axios';
import CustomEdge from '../components/CustomEdge';

// Location node with resize capability
const LocationNode = ({ data, selected }) => {
  const borderColor = data.border_color || '#1976d2';
  const fillColor = data.fill_color || '#FFFFFF';
  const opacity = (data.opacity !== undefined ? data.opacity : 20) / 100; // Convert 0-100 to 0-1

  return (
    <div
      style={{
        padding: '15px',
        borderRadius: '8px',
        border: `3px solid ${borderColor}`,
        background: `${fillColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`, // Add alpha channel
        minWidth: '200px',
        minHeight: '150px',
        width: '100%',
        height: '100%',
        position: 'relative',
        boxSizing: 'border-box',
        overflow: 'visible'
      }}
    >
      <NodeResizer
        color={borderColor}
        isVisible={selected}
        minWidth={200}
        minHeight={150}
        lineClassName="resize-line"
        handleClassName="resize-handle"
      />
      <div style={{
        fontWeight: 'bold',
        marginBottom: '8px',
        color: borderColor,
        fontSize: '16px',
        pointerEvents: 'none'
      }}>
        📍 {data.label}
      </div>
      {data.description && (
        <div style={{
          fontSize: '12px',
          color: '#666',
          pointerEvents: 'none'
        }}>
          {data.description}
        </div>
      )}
    </div>
  );
};

// Device node with image from device type
const DeviceNode = ({ data, selected }) => {
  const hasImage = data.device_type_image_url || data.image_url;
  const imageUrl = data.device_type_image_url || data.image_url;

  // Generate 24 handle positions around rectangle perimeter (6 per side)
  const generateHandles = () => {
    const handles = [];
    const pointsPerSide = 6;
    let pointIndex = 1;

    // Top side (6 points) - centered on top edge
    for (let i = 0; i < pointsPerSide; i++) {
      const x = ((i + 1) / (pointsPerSide + 1)) * 100;
      const handleId = `point-${pointIndex++}`;
      const isVisible = selected;
      const size = isVisible ? 10 : 6;

      // Both source and target handles at the same position
      handles.push(
        <Handle
          key={`source-${handleId}`}
          type="source"
          position={Position.Top}
          id={handleId}
          isConnectable={true}
          style={{
            left: `${x}%`,
            top: '0%',
            transform: 'translate(-50%, -50%)',
            background: isVisible ? '#2196f3' : '#4caf50',
            width: `${size}px`,
            height: `${size}px`,
            opacity: isVisible ? 1 : 0.5,
            transition: 'all 0.2s',
            zIndex: 10,
            border: '2px solid white',
            cursor: 'crosshair'
          }}
        />,
        <Handle
          key={`target-${handleId}`}
          type="target"
          position={Position.Top}
          id={handleId}
          isConnectable={true}
          style={{
            left: `${x}%`,
            top: '0%',
            transform: 'translate(-50%, -50%)',
            background: isVisible ? '#2196f3' : '#4caf50',
            width: `${size}px`,
            height: `${size}px`,
            opacity: isVisible ? 1 : 0.5,
            transition: 'all 0.2s',
            zIndex: 10,
            border: '2px solid white',
            cursor: 'crosshair'
          }}
        />
      );
    }

    // Right side (6 points) - centered on right edge
    for (let i = 0; i < pointsPerSide; i++) {
      const y = ((i + 1) / (pointsPerSide + 1)) * 100;
      const handleId = `point-${pointIndex++}`;
      const isVisible = selected;
      const size = isVisible ? 10 : 6;

      handles.push(
        <Handle
          key={`source-${handleId}`}
          type="source"
          position={Position.Right}
          id={handleId}
          isConnectable={true}
          style={{
            left: '100%',
            top: `${y}%`,
            transform: 'translate(-50%, -50%)',
            background: isVisible ? '#2196f3' : '#4caf50',
            width: `${size}px`,
            height: `${size}px`,
            opacity: isVisible ? 1 : 0.5,
            transition: 'all 0.2s',
            zIndex: 10,
            border: '2px solid white',
            cursor: 'crosshair'
          }}
        />,
        <Handle
          key={`target-${handleId}`}
          type="target"
          position={Position.Right}
          id={handleId}
          isConnectable={true}
          style={{
            left: '100%',
            top: `${y}%`,
            transform: 'translate(-50%, -50%)',
            background: isVisible ? '#2196f3' : '#4caf50',
            width: `${size}px`,
            height: `${size}px`,
            opacity: isVisible ? 1 : 0.5,
            transition: 'all 0.2s',
            zIndex: 10,
            border: '2px solid white',
            cursor: 'crosshair'
          }}
        />
      );
    }

    // Bottom side (6 points) - centered on bottom edge
    for (let i = 0; i < pointsPerSide; i++) {
      const x = ((i + 1) / (pointsPerSide + 1)) * 100;
      const handleId = `point-${pointIndex++}`;
      const isVisible = selected;
      const size = isVisible ? 10 : 6;

      handles.push(
        <Handle
          key={`source-${handleId}`}
          type="source"
          position={Position.Bottom}
          id={handleId}
          isConnectable={true}
          style={{
            left: `${x}%`,
            top: '100%',
            transform: 'translate(-50%, -50%)',
            background: isVisible ? '#2196f3' : '#4caf50',
            width: `${size}px`,
            height: `${size}px`,
            opacity: isVisible ? 1 : 0.5,
            transition: 'all 0.2s',
            zIndex: 10,
            border: '2px solid white',
            cursor: 'crosshair'
          }}
        />,
        <Handle
          key={`target-${handleId}`}
          type="target"
          position={Position.Bottom}
          id={handleId}
          isConnectable={true}
          style={{
            left: `${x}%`,
            top: '100%',
            transform: 'translate(-50%, -50%)',
            background: isVisible ? '#2196f3' : '#4caf50',
            width: `${size}px`,
            height: `${size}px`,
            opacity: isVisible ? 1 : 0.5,
            transition: 'all 0.2s',
            zIndex: 10,
            border: '2px solid white',
            cursor: 'crosshair'
          }}
        />
      );
    }

    // Left side (6 points) - centered on left edge
    for (let i = 0; i < pointsPerSide; i++) {
      const y = ((i + 1) / (pointsPerSide + 1)) * 100;
      const handleId = `point-${pointIndex++}`;
      const isVisible = selected;
      const size = isVisible ? 10 : 6;

      handles.push(
        <Handle
          key={`source-${handleId}`}
          type="source"
          position={Position.Left}
          id={handleId}
          isConnectable={true}
          style={{
            left: '0%',
            top: `${y}%`,
            transform: 'translate(-50%, -50%)',
            background: isVisible ? '#2196f3' : '#4caf50',
            width: `${size}px`,
            height: `${size}px`,
            opacity: isVisible ? 1 : 0.5,
            transition: 'all 0.2s',
            zIndex: 10,
            border: '2px solid white',
            cursor: 'crosshair'
          }}
        />,
        <Handle
          key={`target-${handleId}`}
          type="target"
          position={Position.Left}
          id={handleId}
          isConnectable={true}
          style={{
            left: '0%',
            top: `${y}%`,
            transform: 'translate(-50%, -50%)',
            background: isVisible ? '#2196f3' : '#4caf50',
            width: `${size}px`,
            height: `${size}px`,
            opacity: isVisible ? 1 : 0.5,
            transition: 'all 0.2s',
            zIndex: 10,
            border: '2px solid white',
            cursor: 'crosshair'
          }}
        />
      );
    }

    return handles;
  };

  return (
    <div
      style={{
        padding: hasImage ? '8px' : '10px',
        borderRadius: '8px',
        border: selected ? '3px solid #2196f3' : '2px solid #4caf50',
        background: 'white',
        minWidth: '100px',
        maxWidth: '180px',
        boxShadow: selected ? '0 4px 12px rgba(33, 150, 243, 0.3)' : '0 2px 8px rgba(0,0,0,0.15)',
        cursor: 'grab',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        position: 'relative'
      }}
    >
      {/* 24 Connection handles around the perimeter */}
      {generateHandles()}

      {imageUrl && (
        <img
          src={imageUrl}
          alt={data.label}
          style={{
            width: '50px',
            height: '50px',
            marginBottom: '8px',
            borderRadius: '6px',
            objectFit: 'contain'
          }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      )}
      <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '4px' }}>
        {data.label}
      </div>
      {data.device_type_name && (
        <div style={{ fontSize: '10px', color: '#666', marginBottom: '4px' }}>
          {data.device_type_name}
        </div>
      )}
      {data.ip_address && (
        <Chip
          label={data.ip_address}
          size="small"
          sx={{ height: '18px', fontSize: '9px', mt: 0.5 }}
          color="primary"
          variant="outlined"
        />
      )}
    </div>
  );
};

const nodeTypes = {
  location: LocationNode,
  device: DeviceNode
};

const edgeTypes = {
  custom: CustomEdge
};

function MapView() {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [devices, setDevices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [connections, setConnections] = useState([]);
  const [connectionTypes, setConnectionTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saveDialog, setSaveDialog] = useState(false);
  const [resizeDialog, setResizeDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [resizeValues, setResizeValues] = useState({ width: 250, height: 200 });
  const [isLocked, setIsLocked] = useState(true);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);

  useEffect(() => {
    loadMapData();
  }, []);

  // Update edges when lock state changes
  useEffect(() => {
    setEdges((eds) =>
      eds.map((edge) => ({
        ...edge,
        data: {
          ...edge.data,
          isLocked: isLocked
        }
      }))
    );
  }, [isLocked, setEdges]);

  const loadMapData = async () => {
    try {
      setLoading(true);
      setError('');

      const [devicesRes, locationsRes, connectionsRes, connectionTypesRes, layoutsRes] = await Promise.all([
        axios.get('/devices/'),
        axios.get('/locations/'),
        axios.get('/connections/'),
        axios.get('/connection-types/'),
        axios.get('/map-layouts/')
      ]);

      const devicesData = devicesRes.data;
      const locationsData = locationsRes.data;
      const connectionsData = connectionsRes.data;
      const connectionTypesData = connectionTypesRes.data;
      const layoutsData = layoutsRes.data;

      setDevices(devicesData.items || devicesData);
      setLocations(locationsData);
      setConnections(connectionsData);
      setConnectionTypes(connectionTypesData);

      buildMapVisualization(
        devicesData.items || devicesData,
        locationsData,
        connectionsData,
        connectionTypesData,
        layoutsData
      );
    } catch (err) {
      setError('Ошибка загрузки данных карты: ' + (err.message || 'Неизвестная ошибка'));
      console.error('Map load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const buildMapVisualization = (devices, locations, connections, connectionTypesData, layouts) => {
    const newNodes = [];
    const newEdges = [];
    const layoutMap = {};

    // Create layout lookup map
    layouts.forEach(layout => {
      const key = `${layout.entity_type}_${layout.entity_id}`;
      layoutMap[key] = layout;
    });

    // Create connection type lookup map
    const connectionTypeMap = {};
    connectionTypesData.forEach(type => {
      connectionTypeMap[type.id] = type;
    });

    // Debug: log connection types to verify fields
    console.log('Connection types loaded:', connectionTypesData.map(t => ({
      id: t.id,
      name: t.name,
      color: t.color,
      line_width: t.line_width,
      line_style: t.line_style,
      direction: t.direction
    })));

    // Build location hierarchy map
    const locationChildren = {};
    locations.forEach(loc => {
      if (loc.parent_id) {
        if (!locationChildren[loc.parent_id]) {
          locationChildren[loc.parent_id] = [];
        }
        locationChildren[loc.parent_id].push(loc);
      }
    });

    // Add location nodes (both parent and child)
    // Sort locations so that parent locations are rendered first (children on top)
    const sortedLocations = [...locations].sort((a, b) => {
      // Calculate depth level (root=0, child=1, grandchild=2, etc.)
      const getDepth = (loc) => {
        let depth = 0;
        let current = loc;
        while (current.parent_id) {
          depth++;
          current = locations.find(l => l.id === current.parent_id);
          if (!current) break; // Safety check
        }
        return depth;
      };

      return getDepth(a) - getDepth(b); // Parents first, then children
    });

    sortedLocations.forEach((loc, index) => {
      const layoutKey = `location_${loc.id}`;
      const layout = layoutMap[layoutKey];
      const width = layout?.width || 300;
      const height = layout?.height || 250;

      // Calculate position for child locations
      let position;
      if (loc.parent_id) {
        // This is a child location
        const parentLayout = layoutMap[`location_${loc.parent_id}`];
        if (parentLayout && layout) {
          // Use saved position (relative to parent)
          position = { x: layout.x, y: layout.y };
        } else {
          // Default position inside parent
          const childIndex = locationChildren[loc.parent_id]?.indexOf(loc) || 0;
          position = {
            x: 20 + (childIndex % 2) * 140,
            y: 60 + Math.floor(childIndex / 2) * 140
          };
        }
      } else {
        // This is a root location
        position = layout
          ? { x: layout.x, y: layout.y }
          : { x: 50 + (index % 3) * 350, y: 50 + Math.floor(index / 3) * 300 };
      }

      // Calculate depth for z-index (deeper children have higher z-index)
      let depth = 0;
      let current = loc;
      while (current.parent_id) {
        depth++;
        current = locations.find(l => l.id === current.parent_id);
        if (!current) break;
      }

      newNodes.push({
        id: `location-${loc.id}`,
        type: 'location',
        data: {
          label: loc.name,
          description: loc.description,
          location_id: loc.id,
          parent_id: loc.parent_id,
          border_color: loc.border_color,
          fill_color: loc.fill_color,
          opacity: loc.opacity
        },
        position: position,
        style: {
          width: width,
          height: height,
          zIndex: 1 + depth * 10 // Root=1, child=11, grandchild=21, etc.
        },
        parentNode: loc.parent_id ? `location-${loc.parent_id}` : undefined,
        extent: loc.parent_id ? 'parent' : undefined, // Constrain to parent
        draggable: true,
        expandParent: false
      });
    });

    // Add device nodes
    devices.forEach((device, index) => {
      const layoutKey = `device_${device.id}`;
      const layout = layoutMap[layoutKey];

      // Get IP address from characteristics
      let ipAddress = null;
      if (device.characteristic_values) {
        const ipChar = device.characteristic_values.find(cv =>
          cv.characteristic_name && cv.characteristic_name.toLowerCase().includes('ip')
        );
        if (ipChar) {
          ipAddress = ipChar.value;
        }
      }

      // Calculate device position
      // Important: devices DO NOT use parentNode (to allow edges between different locations)
      // So we need to calculate ABSOLUTE positions based on location hierarchy
      let absolutePosition;

      if (layout) {
        // Has saved layout - these are absolute coordinates from previous save
        absolutePosition = { x: layout.x, y: layout.y };
      } else {
        // No saved layout - calculate position
        if (device.location_id) {
          // Device is in a location - need to calculate absolute position
          const locationNode = newNodes.find(n => n.id === `location-${device.location_id}`);
          if (locationNode) {
            // Get absolute position of the location by traversing hierarchy
            let absoluteLocX = locationNode.position.x;
            let absoluteLocY = locationNode.position.y;

            // If location has a parent, add parent's absolute position
            if (locationNode.parentNode) {
              const parentNode = newNodes.find(n => n.id === locationNode.parentNode);
              if (parentNode) {
                // Recursively get parent's absolute position
                let currentParent = parentNode;
                while (currentParent) {
                  absoluteLocX += currentParent.position.x;
                  absoluteLocY += currentParent.position.y;

                  if (currentParent.parentNode) {
                    currentParent = newNodes.find(n => n.id === currentParent.parentNode);
                  } else {
                    currentParent = null;
                  }
                }
              }
            }

            // Place device at absolute position (location's absolute + relative offset)
            const relativeX = 20 + (index % 3) * 160;
            const relativeY = 70 + Math.floor(index / 3) * 100;
            absolutePosition = {
              x: absoluteLocX + relativeX,
              y: absoluteLocY + relativeY
            };
          } else {
            // Location not found, use default position
            absolutePosition = {
              x: 20 + (index % 3) * 160,
              y: 70 + Math.floor(index / 3) * 100
            };
          }
        } else {
          // Device without location - use default position
          absolutePosition = {
            x: 50 + (index % 4) * 200,
            y: 50 + Math.floor(index / 4) * 150
          };
        }
      }

      newNodes.push({
        id: `device-${device.id}`,
        type: 'device',
        data: {
          label: device.name,
          device_id: device.id,
          device_type_name: device.device_type_name,
          device_type_image_url: device.device_type_image_url, // Image from device type
          location_id: device.location_id,
          image_url: device.image_url,
          ip_address: ipAddress
        },
        position: absolutePosition,
        // Removed parentNode to allow edges to be displayed
        // parentNode: device.location_id ? `location-${device.location_id}` : undefined,
        draggable: true,
        style: {
          zIndex: 1000 // Devices always on top of all locations
        }
      });
    });

    // Add connection edges with handle support (24 points)
    // Track used handles per device
    const usedHandles = {}; // { deviceId: [handle1, handle2, ...] }

    connections.forEach((conn, index) => {
      const sourceDeviceId = `device-${conn.source_device_id}`;
      const targetDeviceId = `device-${conn.target_device_id}`;

      // Check if both devices exist in nodes
      const sourceExists = newNodes.find(n => n.id === sourceDeviceId);
      const targetExists = newNodes.find(n => n.id === targetDeviceId);

      if (sourceExists && targetExists) {
        // Initialize used handles tracking
        if (!usedHandles[sourceDeviceId]) usedHandles[sourceDeviceId] = [];
        if (!usedHandles[targetDeviceId]) usedHandles[targetDeviceId] = [];

        // Use saved handles if available, otherwise auto-assign sequential points
        let sourceHandle = conn.source_handle;
        let targetHandle = conn.target_handle;

        // Auto-assign source handle if not set
        if (!sourceHandle) {
          // Find next available point (point-1, point-2, ..., point-24)
          for (let i = 1; i <= 24; i++) {
            const pointId = `point-${i}`;
            if (!usedHandles[sourceDeviceId].includes(pointId)) {
              sourceHandle = pointId;
              break;
            }
          }
          // Fallback if all points are used (shouldn't happen with 24 points)
          if (!sourceHandle) sourceHandle = `point-${(index % 24) + 1}`;
        }

        // Auto-assign target handle if not set
        if (!targetHandle) {
          for (let i = 1; i <= 24; i++) {
            const pointId = `point-${i}`;
            if (!usedHandles[targetDeviceId].includes(pointId)) {
              targetHandle = pointId;
              break;
            }
          }
          if (!targetHandle) targetHandle = `point-${(index % 24) + 1}`;
        }

        // Mark handles as used
        usedHandles[sourceDeviceId].push(sourceHandle);
        usedHandles[targetDeviceId].push(targetHandle);

        // Get connection type properties
        const connectionType = connectionTypeMap[conn.connection_type_id];
        const edgeColor = connectionType?.color || '#666666';
        const lineWidth = connectionType?.line_width || 2;
        const lineStyle = connectionType?.line_style || 'solid';
        const direction = connectionType?.direction || 'bidirectional';

        // Debug log for this connection
        if (index === 0) {
          console.log('Sample connection styling:', {
            connection_type_id: conn.connection_type_id,
            connectionType: connectionType,
            edgeColor,
            lineWidth,
            lineStyle,
            direction
          });
        }

        // Convert line_style to strokeDasharray
        let strokeDasharray = '0';
        if (lineStyle === 'dashed') {
          strokeDasharray = '10 5'; // 10px dash, 5px gap
        } else if (lineStyle === 'dotted') {
          strokeDasharray = '2 4'; // 2px dot, 4px gap
        }

        // Determine if edge should have arrow (bidirectional = no arrow, no animation)
        const isAnimated = direction !== 'bidirectional';
        const markerEnd = direction === 'bidirectional' ? undefined : {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
          color: edgeColor
        };

        newEdges.push({
          id: `connection-${conn.id}`,
          source: sourceDeviceId,
          target: targetDeviceId,
          sourceHandle: sourceHandle,
          targetHandle: targetHandle,
          type: 'custom',
          animated: isAnimated,
          label: conn.connection_type_name || `Подключение #${conn.id}`,
          markerEnd: markerEnd,
          style: {
            strokeWidth: lineWidth,
            stroke: edgeColor,
            strokeDasharray: strokeDasharray,
            zIndex: 1000
          },
          zIndex: 1000,
          data: {
            connection_id: conn.id,
            connection_type_id: conn.connection_type_id,
            waypoints: conn.waypoints || [],
            onReload: loadMapData,
            isLocked: isLocked
          }
        });

        console.log(`Added edge for connection ${conn.id}:`, {
          source: sourceDeviceId,
          target: targetDeviceId,
          sourceHandle,
          targetHandle,
          color: edgeColor,
          type: conn.connection_type_name
        });
      } else {
        console.warn(`Connection ${conn.id}: Device not found`, {
          source: sourceDeviceId,
          target: targetDeviceId,
          sourceExists: !!sourceExists,
          targetExists: !!targetExists
        });
      }
    });

    console.log('Map visualization built:', {
      locations: locations.length,
      devices: devices.length,
      connections: connections.length,
      nodes: newNodes.length,
      edges: newEdges.length,
      deviceNodes: newNodes.filter(n => n.type === 'device').length,
      locationNodes: newNodes.filter(n => n.type === 'location').length
    });

    setNodes(newNodes);
    setEdges(newEdges);
  };

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({
      ...params,
      animated: true,
      style: { strokeWidth: 3, stroke: '#FF6B6B' }
    }, eds)),
    [setEdges]
  );

  // Handle edge updates (when user drags connection endpoint to different handle)
  const onEdgeUpdate = useCallback(
    async (oldEdge, newConnection) => {
      try {
        // Extract connection ID from edge
        const connectionId = oldEdge.data?.connection_id;
        if (!connectionId) {
          console.error('No connection_id found in edge data');
          return;
        }

        // Determine which handle was changed
        // newConnection has the new values, oldEdge has the old values
        const sourceHandle = newConnection.sourceHandle || oldEdge.sourceHandle;
        const targetHandle = newConnection.targetHandle || oldEdge.targetHandle;
        const sourceDevice = newConnection.source || oldEdge.source;
        const targetDevice = newConnection.target || oldEdge.target;

        console.log('Edge update - full data:', {
          connectionId,
          oldEdge: {
            source: oldEdge.source,
            target: oldEdge.target,
            sourceHandle: oldEdge.sourceHandle,
            targetHandle: oldEdge.targetHandle
          },
          newConnection: {
            source: newConnection.source,
            target: newConnection.target,
            sourceHandle: newConnection.sourceHandle,
            targetHandle: newConnection.targetHandle
          },
          resolved: {
            sourceDevice,
            targetDevice,
            sourceHandle,
            targetHandle
          }
        });

        // Prepare update payload - only include changed fields
        const updatePayload = {};
        if (sourceHandle !== oldEdge.sourceHandle) {
          updatePayload.source_handle = sourceHandle;
        }
        if (targetHandle !== oldEdge.targetHandle) {
          updatePayload.target_handle = targetHandle;
        }

        console.log('Update payload:', updatePayload);

        // Update connection in database with new handle positions
        const response = await fetch(`/api/connections/${connectionId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify(updatePayload)
        });

        if (!response.ok) {
          throw new Error('Failed to update connection handles');
        }

        // Update the edge in the UI
        setEdges((eds) => {
          const updatedEdges = eds.map((edge) => {
            if (edge.id === oldEdge.id) {
              return {
                ...edge,
                sourceHandle: sourceHandle,
                targetHandle: targetHandle
              };
            }
            return edge;
          });
          return updatedEdges;
        });

        console.log(`Updated connection ${connectionId} handles successfully`);
      } catch (error) {
        console.error('Error updating connection handles:', error);
        // Reload map data to reset the edge
        loadMapData();
      }
    },
    [setEdges]
  );

  // Custom node change handler to:
  // 1. Move devices with their locations (including nested locations)
  // 2. Constrain device movement within location bounds
  const handleNodesChange = useCallback((changes) => {
    let modifiedChanges = [...changes];

    changes.forEach((change, index) => {
      // Handle location movement - move all devices in that location AND child locations
      if (change.type === 'position' && change.id.startsWith('location-') && change.position) {
        const locationId = parseInt(change.id.replace('location-', ''));
        const locationNode = nodes.find(n => n.id === change.id);

        if (locationNode && locationNode.position) {
          const deltaX = change.position.x - locationNode.position.x;
          const deltaY = change.position.y - locationNode.position.y;

          // Find all child locations recursively
          const getAllChildLocationIds = (parentId) => {
            const childIds = [];
            nodes.forEach(node => {
              if (node.type === 'location' && node.data.parent_id === parentId) {
                childIds.push(node.data.location_id);
                // Recursively get children of this child
                childIds.push(...getAllChildLocationIds(node.data.location_id));
              }
            });
            return childIds;
          };

          const affectedLocationIds = [locationId, ...getAllChildLocationIds(locationId)];

          // Update positions of all devices in this location and child locations
          setNodes(currentNodes =>
            currentNodes.map(node => {
              if (node.type === 'device' && affectedLocationIds.includes(node.data.location_id)) {
                return {
                  ...node,
                  position: {
                    x: node.position.x + deltaX,
                    y: node.position.y + deltaY
                  }
                };
              }
              return node;
            })
          );
        }
      }

      // Handle device movement - constrain within location bounds
      if (change.type === 'position' && change.id.startsWith('device-') && change.position) {
        const deviceNode = nodes.find(n => n.id === change.id);
        if (deviceNode && deviceNode.data.location_id) {
          const locationNode = nodes.find(n => n.id === `location-${deviceNode.data.location_id}`);

          if (locationNode) {
            // Calculate absolute position of location (considering parent hierarchy)
            let absoluteLocX = locationNode.position.x;
            let absoluteLocY = locationNode.position.y;

            if (locationNode.parentNode) {
              let currentParent = nodes.find(n => n.id === locationNode.parentNode);
              while (currentParent) {
                absoluteLocX += currentParent.position.x;
                absoluteLocY += currentParent.position.y;

                if (currentParent.parentNode) {
                  currentParent = nodes.find(n => n.id === currentParent.parentNode);
                } else {
                  currentParent = null;
                }
              }
            }

            const locationWidth = locationNode.style?.width || 300;
            const locationHeight = locationNode.style?.height || 250;

            // Device actual size (smaller than the approximation to allow more movement)
            const deviceWidth = 100;  // Reduced from 180 to fix right edge constraint
            const deviceHeight = 80;  // Reduced from 120

            // Constrain device position within location bounds (using absolute coordinates)
            const constrainedX = Math.max(
              absoluteLocX + 10,
              Math.min(change.position.x, absoluteLocX + locationWidth - deviceWidth - 10)
            );
            const constrainedY = Math.max(
              absoluteLocY + 40,
              Math.min(change.position.y, absoluteLocY + locationHeight - deviceHeight - 10)
            );

            modifiedChanges[index] = {
              ...change,
              position: {
                x: constrainedX,
                y: constrainedY
              }
            };
          }
        }
      }
    });

    onNodesChange(modifiedChanges);
  }, [nodes, onNodesChange, setNodes]);

  const handleSaveLayout = async () => {
    try {
      setLoading(true);
      const layoutData = nodes.map(node => {
        const entityType = node.id.startsWith('location-') ? 'location' : 'device';
        const entityId = parseInt(node.id.split('-')[1]);

        return {
          entity_type: entityType,
          entity_id: entityId,
          x: node.position.x,
          y: node.position.y,
          width: node.style?.width || null,
          height: node.style?.height || null,
          z_index: node.style?.zIndex || 0,
          location_id: node.data.location_id || node.data.parent_id || null
        };
      });

      await axios.post('/map-layouts/bulk-save', layoutData);

      alert('Расположение сохранено успешно!');
      setSaveDialog(false);
    } catch (err) {
      setError('Ошибка сохранения: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleExportPNG = async () => {
    setExportAnchorEl(null);
    try {
      const mapElement = document.querySelector('.react-flow');
      if (!mapElement) {
        alert('Карта не найдена');
        return;
      }

      // Use scale 5 for ultra high quality (5x resolution)
      const canvas = await html2canvas(mapElement, {
        backgroundColor: '#ffffff',
        scale: 5, // Ultra high quality - 5x resolution
        logging: false,
        useCORS: true,
        allowTaint: true,
        imageTimeout: 0
      });

      const link = document.createElement('a');
      link.download = `map-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Export error:', err);
      alert('Ошибка экспорта: ' + err.message);
    }
  };

  const handleExportPDF = async () => {
    setExportAnchorEl(null);
    try {
      const mapElement = document.querySelector('.react-flow');
      if (!mapElement) {
        alert('Карта не найдена');
        return;
      }

      // Use scale 5 for PDF as well
      const canvas = await html2canvas(mapElement, {
        backgroundColor: '#ffffff',
        scale: 5, // Ultra high quality
        logging: false,
        useCORS: true,
        allowTaint: true,
        imageTimeout: 0
      });

      const imgData = canvas.toDataURL('image/png');

      // Calculate dimensions for A4 or larger format
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = imgWidth / imgHeight;

      // Use mm and A4 format, but scale to fit
      const pdf = new jsPDF({
        orientation: ratio > 1 ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a3' // Use A3 for better quality
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Scale to fit page while maintaining aspect ratio
      let finalWidth = pageWidth;
      let finalHeight = pageWidth / ratio;

      if (finalHeight > pageHeight) {
        finalHeight = pageHeight;
        finalWidth = pageHeight * ratio;
      }

      pdf.addImage(imgData, 'PNG', 0, 0, finalWidth, finalHeight, '', 'FAST');
      pdf.save(`map-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('Export error:', err);
      alert('Ошибка экспорта: ' + err.message);
    }
  };

  const handleExportSVG = async () => {
    setExportAnchorEl(null);
    try {
      // Get the entire ReactFlow container
      const flowElement = document.querySelector('.react-flow');
      if (!flowElement) {
        alert('Карта не найдена');
        return;
      }

      // Get viewport dimensions
      const viewportElement = document.querySelector('.react-flow__viewport');
      const bbox = viewportElement ? viewportElement.getBBox() : null;

      // Create a new SVG with proper dimensions and styles
      const svgNS = "http://www.w3.org/2000/svg";
      const svg = document.createElementNS(svgNS, "svg");

      // Set dimensions
      const width = bbox ? bbox.width + 100 : 2000;
      const height = bbox ? bbox.height + 100 : 2000;
      svg.setAttribute("width", width);
      svg.setAttribute("height", height);
      svg.setAttribute("viewBox", bbox ? `${bbox.x - 50} ${bbox.y - 50} ${width} ${height}` : `0 0 ${width} ${height}`);
      svg.setAttribute("xmlns", svgNS);

      // Add white background
      const background = document.createElementNS(svgNS, "rect");
      background.setAttribute("width", "100%");
      background.setAttribute("height", "100%");
      background.setAttribute("fill", "white");
      svg.appendChild(background);

      // Clone the viewport content
      const viewportClone = viewportElement ? viewportElement.cloneNode(true) : null;
      if (viewportClone) {
        // Create a group for the viewport
        const g = document.createElementNS(svgNS, "g");

        // Copy all SVG children from viewport
        const svgElements = viewportClone.querySelectorAll('svg > *');
        svgElements.forEach(el => {
          g.appendChild(el.cloneNode(true));
        });

        svg.appendChild(g);
      }

      // Add embedded CSS for better rendering
      const style = document.createElementNS(svgNS, "style");
      style.textContent = `
        .react-flow__edge-path { stroke-width: 2; }
        .react-flow__node { font-family: Arial, sans-serif; }
        text { font-family: Arial, sans-serif; }
      `;
      svg.insertBefore(style, svg.firstChild);

      const svgData = new XMLSerializer().serializeToString(svg);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);

      const link = document.createElement('a');
      link.download = `map-${new Date().toISOString().split('T')[0]}.svg`;
      link.href = svgUrl;
      link.click();

      URL.revokeObjectURL(svgUrl);
    } catch (err) {
      console.error('Export error:', err);
      alert('Ошибка экспорта: ' + err.message);
    }
  };

  return (
    <Box sx={{ width: '100%', height: 'calc(100vh - 100px)' }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5">Интерактивная карта оборудования</Typography>
          <Box>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadMapData}
              disabled={loading}
              sx={{ mr: 1 }}
            >
              Обновить
            </Button>
            <Button
              startIcon={isLocked ? <LockIcon /> : <LockOpenIcon />}
              variant={isLocked ? "contained" : "outlined"}
              onClick={() => setIsLocked(!isLocked)}
              color={isLocked ? "warning" : "primary"}
              sx={{ mr: 1 }}
            >
              {isLocked ? 'Разблокировать' : 'Заблокировать'}
            </Button>
            <Button
              startIcon={<SaveIcon />}
              variant="contained"
              onClick={() => setSaveDialog(true)}
              disabled={loading || isLocked}
              sx={{ mr: 1 }}
            >
              Сохранить расположение
            </Button>
            <Button
              startIcon={<DownloadIcon />}
              variant="outlined"
              onClick={(e) => setExportAnchorEl(e.currentTarget)}
              disabled={loading}
            >
              Экспорт
            </Button>
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={() => setExportAnchorEl(null)}
            >
              <MenuItem onClick={handleExportPNG}>Экспорт в PNG</MenuItem>
              <MenuItem onClick={handleExportPDF}>Экспорт в PDF</MenuItem>
              <MenuItem onClick={handleExportSVG}>Экспорт в SVG</MenuItem>
            </Menu>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip icon={<span>📍</span>} label={`Локаций: ${locations.length}`} />
          <Chip icon={<span>🖥️</span>} label={`Устройств: ${devices.length}`} color="success" />
          <Chip icon={<span>🔗</span>} label={`Подключений: ${connections.length}`} color="primary" />
          <Chip
            label="Подсказка: выделите локацию для изменения размера"
            variant="outlined"
            size="small"
            sx={{ ml: 'auto' }}
          />
        </Box>
      </Paper>

      <Paper sx={{ height: 'calc(100% - 180px)', position: 'relative' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography>Загрузка карты...</Typography>
          </Box>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={isLocked ? undefined : handleNodesChange}
            onEdgesChange={isLocked ? undefined : onEdgesChange}
            onConnect={isLocked ? undefined : onConnect}
            onEdgeUpdate={isLocked ? undefined : onEdgeUpdate}
            onReconnect={isLocked ? undefined : onEdgeUpdate}
            onEdgeUpdateStart={isLocked ? undefined : (event, edge) => {
              console.log('Edge update started:', edge);
            }}
            onEdgeUpdateEnd={isLocked ? undefined : (event, edge) => {
              console.log('Edge update ended:', edge);
            }}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            attributionPosition="bottom-left"
            minZoom={0.1}
            maxZoom={2}
            connectionLineType={ConnectionLineType.SmoothStep}
            connectionRadius={30}
            edgesUpdatable={!isLocked}
            edgesFocusable={!isLocked}
            elevateEdgesOnSelect={!isLocked}
            reconnectRadius={isLocked ? 0 : 30}
            edgesReconnectable={!isLocked}
            nodesDraggable={!isLocked}
            nodesConnectable={!isLocked}
            elementsSelectable={true}
            panOnDrag={[1, 2]}
            selectionOnDrag={false}
            panOnScroll={false}
            zoomOnScroll={true}
            zoomOnPinch={true}
            zoomOnDoubleClick={false}
            defaultEdgeOptions={{
              animated: true,
              style: { strokeWidth: 3 },
              type: 'smoothstep'
            }}
          >
            <MiniMap
              nodeColor={(node) => {
                if (node.type === 'location') return '#1976d2';
                if (node.type === 'device') return '#4caf50';
                return '#ccc';
              }}
              style={{ background: '#f5f5f5' }}
              zoomable
              pannable
            />
            <Controls />
            <Background color="#aaa" gap={16} />
            <Panel position="top-right">
              <Paper sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                  Легенда:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 16, border: '2px solid #1976d2', borderRadius: 1 }} />
                  <Typography variant="caption">Локация</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 16, border: '2px solid #4caf50', borderRadius: 1, bgcolor: 'white' }} />
                  <Typography variant="caption">Устройство</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 2, bgcolor: '#FF6B6B' }} />
                  <Typography variant="caption">Подключение</Typography>
                </Box>
              </Paper>
            </Panel>
          </ReactFlow>
        )}
      </Paper>

      {/* Save confirmation dialog */}
      <Dialog open={saveDialog} onClose={() => setSaveDialog(false)}>
        <DialogTitle>Сохранить расположение?</DialogTitle>
        <DialogContent>
          <Typography>
            Текущее расположение {nodes.length} элементов на карте будет сохранено в базу данных.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Сохраняются позиции и размеры всех локаций и устройств.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialog(false)}>Отмена</Button>
          <Button onClick={handleSaveLayout} variant="contained" disabled={loading}>
            {loading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MapView;
